import { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import Sidebar from "./sidebar";
import { userService } from '../services/user.service';
import { postService } from '../services/post.service';
import { authService } from '../services/auth.service';
import { CardSkeleton } from './Skeleton';
import { ThemeToggle } from './ThemeToggle';

interface DashboardStats {
  totalUsers: number;
  totalPosts: number;
  activePosts: number;
  inactivePosts: number;
  totalContacts: number;
  pendingContacts: number;
}

export default function DashboardLayout() {
  const location = useLocation();
  const [stats, setStats] = useState<DashboardStats>({
    totalUsers: 0,
    totalPosts: 0,
    activePosts: 0,
    inactivePosts: 0,
    totalContacts: 0,
    pendingContacts: 0,
  });
  const [loading, setLoading] = useState(true);

  const isDashboardHome = location.pathname === "/dashboard";

  useEffect(() => {
    if (isDashboardHome) {
      loadStats();
    }
  }, [isDashboardHome]);

  const loadStats = async () => {
    try {
      setLoading(true);

      const usersResponse = await userService.getAllUsers({ page: 1, limit: 1 });
      const postsResponse = await postService.getAllPosts({ page: 1, limit: 1000 });
      const posts = postsResponse.posts || postsResponse.data || [];

      const contactsResponse = await authService.makeAuthenticatedRequest('/contact');
      const contactsData = contactsResponse.ok ? await contactsResponse.json() : { items: [] };
      const contacts = contactsData.items || [];

      setStats({
        totalUsers: usersResponse.total || 0,
        totalPosts: posts.length,
        activePosts: posts.filter((p: any) => p.status === 'ACTIVE').length,
        inactivePosts: posts.filter((p: any) => p.status === 'INACTIVE').length,
        totalContacts: contacts.length,
        pendingContacts: contacts.filter((c: any) => c.status === 'OPEN').length,
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        minHeight: "100vh",
        background: "var(--background)",
      }}
    >
      <Sidebar />
      <main style={{ flex: 1, padding: 40, maxWidth: "1600px", margin: "0 auto", width: "100%" }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 32,
          position: 'sticky',
          top: 0,
          paddingTop: 12,
          paddingBottom: 12,
          background: 'var(--background)',
          zIndex: 10,
          borderBottom: "1px solid var(--border)",
        }}>
          <div>
            <h1
              style={{
                fontSize: 28,
                fontWeight: 800,
                color: "var(--card-foreground)",
                margin: 0,
              }}
            >
              Dashboard
            </h1>
          </div>
          <ThemeToggle />
        </div>

        {isDashboardHome ? (
          <div>
            <div style={{ marginBottom: 36 }}>
              <p style={{ color: 'var(--muted-foreground)', fontSize: 16, lineHeight: 1.6 }}>
                Bem-vindo ao painel de controle. Aqui você pode gerenciar todos os aspectos do sistema.
              </p>
            </div>

                        <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                gap: 28,
                marginBottom: 40,
              }}
            >
              {loading ? (
                <>
                  <CardSkeleton />
                  <CardSkeleton />
                  <CardSkeleton />
                  <CardSkeleton />
                  <CardSkeleton />
                  <CardSkeleton />
                </>
              ) : (
                <>
                  <div
                    style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      borderRadius: 16,
                      padding: 28,
                      color: 'white',
                      boxShadow: '0 8px 24px rgba(102, 126, 234, 0.35)',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      cursor: 'pointer',
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-8px)';
                      e.currentTarget.style.boxShadow = '0 12px 32px rgba(102, 126, 234, 0.45)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 8px 24px rgba(102, 126, 234, 0.35)';
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                      <span style={{ fontSize: 15, opacity: 0.95, fontWeight: 600 }}>Total de Usuários</span>
                      <div style={{ width: 48, height: 48, borderRadius: 12, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <svg width="28" height="28" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                      </div>
                    </div>
                    <div style={{ fontSize: 42, fontWeight: 800, letterSpacing: '-1px' }}>{stats.totalUsers}</div>
                    <div style={{ fontSize: 13, opacity: 0.85, marginTop: 8 }}>Usuários cadastrados</div>
                  </div>

                  <div
                    style={{
                      background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                      borderRadius: 16,
                      padding: 28,
                      color: 'white',
                      boxShadow: '0 8px 24px rgba(240, 147, 251, 0.35)',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      cursor: 'pointer',
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-8px)';
                      e.currentTarget.style.boxShadow = '0 12px 32px rgba(240, 147, 251, 0.45)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 8px 24px rgba(240, 147, 251, 0.35)';
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                      <span style={{ fontSize: 15, opacity: 0.95, fontWeight: 600 }}>Total de Publicações</span>
                      <div style={{ width: 48, height: 48, borderRadius: 12, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <svg width="28" height="28" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                        </svg>
                      </div>
                    </div>
                    <div style={{ fontSize: 42, fontWeight: 800, letterSpacing: '-1px' }}>{stats.totalPosts}</div>
                    <div style={{ fontSize: 13, opacity: 0.85, marginTop: 8 }}>Publicações totais</div>
                  </div>

                  <div
                    style={{
                      background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                      borderRadius: 16,
                      padding: 28,
                      color: 'white',
                      boxShadow: '0 8px 24px rgba(79, 172, 254, 0.35)',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      cursor: 'pointer',
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-8px)';
                      e.currentTarget.style.boxShadow = '0 12px 32px rgba(79, 172, 254, 0.45)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 8px 24px rgba(79, 172, 254, 0.35)';
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                      <span style={{ fontSize: 15, opacity: 0.95, fontWeight: 600 }}>Publicações Ativas</span>
                      <div style={{ width: 48, height: 48, borderRadius: 12, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <svg width="28" height="28" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                    </div>
                    <div style={{ fontSize: 42, fontWeight: 800, letterSpacing: '-1px' }}>{stats.activePosts}</div>
                    <div style={{ fontSize: 13, opacity: 0.85, marginTop: 8 }}>Publicações ativas</div>
                  </div>

                  <div
                    style={{
                      background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                      borderRadius: 16,
                      padding: 28,
                      color: 'white',
                      boxShadow: '0 8px 24px rgba(250, 112, 154, 0.35)',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      cursor: 'pointer',
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-8px)';
                      e.currentTarget.style.boxShadow = '0 12px 32px rgba(250, 112, 154, 0.45)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 8px 24px rgba(250, 112, 154, 0.35)';
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                      <span style={{ fontSize: 15, opacity: 0.95, fontWeight: 600 }}>Publicações Inativas</span>
                      <div style={{ width: 48, height: 48, borderRadius: 12, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <svg width="28" height="28" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                    </div>
                    <div style={{ fontSize: 42, fontWeight: 800, letterSpacing: '-1px' }}>{stats.inactivePosts}</div>
                    <div style={{ fontSize: 13, opacity: 0.85, marginTop: 8 }}>Publicações inativas</div>
                  </div>

                  <div
                    style={{
                      background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                      borderRadius: 16,
                      padding: 28,
                      color: 'white',
                      boxShadow: '0 8px 24px rgba(67, 233, 123, 0.35)',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      cursor: 'pointer',
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-8px)';
                      e.currentTarget.style.boxShadow = '0 12px 32px rgba(67, 233, 123, 0.45)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 8px 24px rgba(67, 233, 123, 0.35)';
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                      <span style={{ fontSize: 15, opacity: 0.95, fontWeight: 600 }}>Total de Contatos</span>
                      <div style={{ width: 48, height: 48, borderRadius: 12, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <svg width="28" height="28" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      </div>
                    </div>
                    <div style={{ fontSize: 42, fontWeight: 800, letterSpacing: '-1px' }}>{stats.totalContacts}</div>
                    <div style={{ fontSize: 13, opacity: 0.85, marginTop: 8 }}>Mensagens recebidas</div>
                  </div>

                  <div
                    style={{
                      background: 'linear-gradient(135deg, #ff9a56 0%, #ff6a88 100%)',
                      borderRadius: 16,
                      padding: 28,
                      color: 'white',
                      boxShadow: '0 8px 24px rgba(255, 154, 86, 0.35)',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      cursor: 'pointer',
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-8px)';
                      e.currentTarget.style.boxShadow = '0 12px 32px rgba(255, 154, 86, 0.45)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 8px 24px rgba(255, 154, 86, 0.35)';
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                      <span style={{ fontSize: 15, opacity: 0.95, fontWeight: 600 }}>Contatos Pendentes</span>
                      <div style={{ width: 48, height: 48, borderRadius: 12, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <svg width="28" height="28" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                    </div>
                    <div style={{ fontSize: 42, fontWeight: 800, letterSpacing: '-1px' }}>{stats.pendingContacts}</div>
                    <div style={{ fontSize: 13, opacity: 0.85, marginTop: 8 }}>Aguardando resposta</div>
                  </div>
                </>
              )}
            </div>
          </div>
        ) : (
          <Outlet />
        )}
      </main>
    </div>
  );
}
